import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import RuleIcon from '@mui/icons-material/Rule'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import moment from 'moment'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'
import Loading from '../../loading/loadingPage'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import NumberCardTwo from '../../../components/Cards/NumberCardTwo'
import EmptyState from '../../../components/EmptyState/EmptyState'
import Age from '../../../components/Age'
import AuditDialog from './AuditDialog'

// Secondary columns hidden on phones (still reachable via the grid's column picker)
const MOBILE_HIDDEN_COLUMNS = {
  date: false,
  auditedBy: false,
  completedAt: false,
}

// Read-only view of completed audits on records the signed-in user took part
// in - their audit outcomes and any feedback the Governance team left them.
const MyAudits = () => {
  const isMobile = useIsMobile()
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({})
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [audits, setAudits] = useState([])
  const [selectedAudit, setSelectedAudit] = useState(null)

  const loadAudits = () => {
    API.get('/my-epcr-audits')
      .then((res) => {
        if (res.status === 200) {
          setAudits(res.data.audits ?? [])
        } else {
          setFetchError(true)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadAudits()
  }, [])

  const markReviewed = (audit) =>
    API.post(`/my-epcr-audits/${audit._id}/reviewed`).then((res) => {
      if (res.status === 200) {
        setSelectedAudit(null)
        loadAudits()
      }
    })

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? MOBILE_HIDDEN_COLUMNS : {})
  }, [isMobile])

  const compliantCount = audits.filter(
    (audit) => audit.overallCompliant,
  ).length
  const feedbackCount = audits.filter((audit) => audit.feedback).length
  const toReviewCount = audits.filter(
    (audit) => audit.isOwner && !audit.reviewedByOwner,
  ).length

  const columns = useMemo(
    () => [
      {
        field: 'epcrNumber',
        headerName: isMobile ? 'ePCR' : 'ePCR No',
        minWidth: isMobile ? 84 : 100,
        valueGetter: (value, row) => row.epcr?.epcrNumber ?? '',
      },
      {
        field: 'date',
        headerName: 'Incident Date',
        minWidth: 140,
        valueGetter: (value, row) =>
          moment(row.epcr?.incident?.incidentDate ?? row.epcr?.createdAt).unix(),
        renderCell: ({ row }) =>
          moment(row.epcr?.incident?.incidentDate ?? row.epcr?.createdAt).format(
            'DD/MM/YYYY',
          ),
      },
      {
        field: 'patient',
        headerName: 'Patient',
        minWidth: isMobile ? 130 : 180,
        flex: 1,
        valueGetter: (value, row) => {
          const demographics = row.epcr?.demographics
          if (!demographics?.firstName && !demographics?.lastName) return ''
          const age = demographics?.dob
            ? ` (${Age(demographics.dob, row.epcr?.createdAt)})`
            : ''
          return `${demographics?.lastName ?? ''}, ${
            demographics?.firstName?.[0] ?? ''
          }${age}`
        },
      },
      {
        field: 'outcome',
        headerName: 'Outcome',
        minWidth: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) =>
          row.overallCompliant ? 'Compliant' : 'Not Compliant',
        renderCell: ({ row }) => (
          <Chip
            label={row.overallCompliant ? 'Compliant' : 'Not Compliant'}
            size="small"
            color={row.overallCompliant ? 'success' : 'error'}
            sx={{ borderRadius: 1 }}
          />
        ),
      },
      {
        field: 'review',
        headerName: 'Review',
        minWidth: 120,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) =>
          row.reviewedByOwner
            ? 'Reviewed'
            : row.isOwner
              ? 'To Review'
              : '',
        renderCell: ({ row }) =>
          row.reviewedByOwner ? (
            <Chip
              label="Reviewed"
              size="small"
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          ) : row.isOwner ? (
            <Tooltip title="Open this audit and mark it as reviewed">
              <Chip
                label="To Review"
                size="small"
                color="warning"
                sx={{ borderRadius: 1 }}
              />
            </Tooltip>
          ) : (
            ''
          ),
      },
      {
        field: 'feedback',
        headerName: 'Feedback',
        minWidth: 120,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => (row.feedback ? 'Yes' : ''),
        renderCell: ({ row }) =>
          row.feedback ? (
            <Box display="flex" height="100%" alignItems="center">
              <Tooltip title="The auditor left you feedback - open the audit to read it">
                <Chip
                  icon={<ChatBubbleOutlineIcon />}
                  label="Feedback"
                  size="small"
                  color="info"
                  sx={{ borderRadius: 1 }}
                />
              </Tooltip>
            </Box>
          ) : (
            ''
          ),
      },
      {
        field: 'auditedBy',
        headerName: 'Audited By',
        minWidth: 150,
        valueGetter: (value, row) =>
          row.completedBy
            ? `${row.completedBy.firstName} ${row.completedBy.lastName}`
            : '',
      },
      {
        field: 'completedAt',
        headerName: 'Completed',
        minWidth: 130,
        valueGetter: (value, row) => moment(row.completedAt).unix(),
        renderCell: ({ row }) => moment(row.completedAt).format('DD/MM/YYYY'),
      },
    ],
    [isMobile],
  )

  if (loading) return <Loading />

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="title2">My Audits</Typography>
        <Typography variant="body2" color="text.secondary">
          Completed audits of patient records you were involved in, including
          any feedback from the auditor.
        </Typography>
      </Grid>

      {fetchError && (
        <Grid item xs={12}>
          <Alert severity="error">Unable to load your audits</Alert>
        </Grid>
      )}

      {!fetchError &&
        (audits.length === 0 ? (
          <Grid item xs={12}>
            <EmptyState
              icon={RuleIcon}
              title="No audits of your records yet"
              description="When the Governance team completes an audit of a patient record you were involved in, the outcome and any feedback for you will appear here."
              sx={{ boxSizing: 'border-box' }}
            />
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <NumberCardTwo
                  number={audits.length}
                  label="Audits Completed"
                  color={4}
                  tooltipTitle="Completed audits of records you were involved in"
                />
                <NumberCardTwo
                  number={`${Math.round((compliantCount / audits.length) * 100)}%`}
                  label="Compliance Rate"
                  color={9}
                  tooltipTitle="Audits where the record met every criterion"
                />
                <NumberCardTwo
                  number={feedbackCount}
                  label="With Feedback"
                  color={3}
                  tooltipTitle="Audits where the auditor left you feedback"
                />
                <NumberCardTwo
                  number={toReviewCount}
                  label="To Review"
                  color={toReviewCount > 0 ? 1 : 4}
                  tooltipTitle="Audits of records you own that you haven't marked as reviewed yet"
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <CustomDataGrid
                rows={audits}
                columns={columns}
                getRowId={(row) => row._id}
                maxHeight={600}
                hideFilters
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                disableRowSelectionOnClick
                onRowClick={({ row }) => setSelectedAudit(row)}
                sx={{
                  '.MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-row:hover': {
                    cursor: 'pointer',
                  },
                }}
              />
            </Grid>
          </>
        ))}

      <AuditDialog
        audit={selectedAudit}
        preventSelfAudit={false}
        hideOpenRecord
        onClose={() => setSelectedAudit(null)}
        onCompleted={() => setSelectedAudit(null)}
        onMarkReviewed={
          selectedAudit?.isOwner && !selectedAudit?.reviewedByOwner
            ? markReviewed
            : undefined
        }
      />
    </Grid>
  )
}

export default MyAudits
